import { ArrowRight, X } from "lucide-react";
import { Avatar, Button, PlatformIcon, type Platform } from "@/shared/ui";
import { fmtNum, fmtRub, fmtRubFull } from "@/shared/lib/format";
import { type Blogger, type Placement } from "@/shared/api/wb";

// Static expansion recommendations from seed_data.EXPANSION_RECS
const RECS: { blogger_id: string; reason: string; based_on: string }[] = [
  { blogger_id: "b14", reason: "аудитория совпадает с @velya на 81%", based_on: "b08" },
  { blogger_id: "b11", reason: "тот же формат, что у @startup_news", based_on: "b15" },
  { blogger_id: "b10", reason: "микро-инфлюенс, ER 6.9% — выше среднего", based_on: "b08" },
];

export function ExpansionPanel({ onClose, onOpenCatalog, placements, bloggers }: {
  onClose: () => void;
  /** Recommendations are informational only — adding a channel always goes
   * through the catalog, so format/creative/date get configured the same
   * way as any other placement. */
  onOpenCatalog: (bloggerIds: string[]) => void;
  placements: Placement[];
  bloggers: Map<string, Blogger>;
}) {
  const topPerformers = placements.filter((p) => p.top_performer || (p.result && p.result.cpv < 0.5));
  const totalAdd = RECS.reduce((s, r) => s + (bloggers.get(r.blogger_id)?.price ?? 0), 0);

  function openOne(bid: string) {
    onOpenCatalog([bid]);
  }
  function openAll() {
    onOpenCatalog(RECS.map((r) => r.blogger_id));
  }

  return (
    <div className="fixed inset-0 z-[80] flex justify-end" style={{ background: "rgba(28,28,28,0.40)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div className="w-[560px] bg-[var(--bg)] h-full flex flex-col shadow-lg fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="px-7 py-6 border-b border-ink-12 flex items-start justify-between flex-shrink-0">
          <div>
            <div className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] text-ink-40 mb-1.5">Расширить РК</div>
            <h2 className="font-display font-extrabold text-[22px] tracking-tight">Эти каналы сработали — попробуйте похожие</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-md text-ink-60 hover:bg-ink-6"><X size={18} /></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="px-7 py-5 border-b border-ink-12">
            <div className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] text-ink-40 mb-3">Опираемся на</div>
            <div className="grid gap-2">
              {topPerformers.map((p) => {
                const b = bloggers.get(p.blogger_id);
                if (!b) return null;
                return (
                  <div key={p.id} className="flex items-center gap-3 px-2.5 py-2 rounded-md bg-cream">
                    <Avatar name={b.full} size={28} grad={b.grad as [string, string]} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold">{b.name}</div>
                      <div className="text-[11.5px] text-ink-40">
                        CPV <b className="text-success font-mono">{p.result?.cpv}₽</b> · {fmtNum(p.result?.views ?? 0)} просмотров
                        {p.top_performer && " · ★ топ"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="px-7 py-5">
            <div className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] text-ink-40 mb-3 flex items-center gap-1.5">
              <span className="text-purple-900">✦</span> Похожие · готовы к добавлению
            </div>
            <div className="grid gap-2.5">
              {RECS.map((r) => {
                const b = bloggers.get(r.blogger_id);
                const basedOn = bloggers.get(r.based_on);
                if (!b) return null;
                return (
                  <div key={r.blogger_id} className="bg-white rounded-md border border-ink-12 p-3.5">
                    <div className="flex items-center gap-3">
                      <Avatar name={b.full} size={40} grad={b.grad as [string, string]} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 font-display font-semibold text-[14px]">
                          <PlatformIcon platform={b.platform as Platform} size={12} /> {b.name}
                        </div>
                        <div className="text-[11.5px] text-ink-40 mt-0.5">
                          {fmtNum(b.reach)} охват · CPV ~{b.cpv}₽ · {fmtRub(b.price)}
                        </div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => openOne(b.id)}><ArrowRight size={12} strokeWidth={3} /> В каталог</Button>
                    </div>
                    <div className="mt-2.5 p-2.5 bg-cream rounded-sm text-[12px] text-ink-60 flex items-start gap-2">
                      <span className="text-purple-900 font-bold">✦</span>
                      <div><b>Похож на {basedOn?.name}:</b> {r.reason}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-7 py-4 border-t border-ink-12 flex gap-2 flex-shrink-0 bg-white">
          <Button variant="dark" className="flex-1" onClick={openAll}>
            <ArrowRight size={14} strokeWidth={3} /> Перейти в каталог · все 3 · {fmtRubFull(totalAdd)}
          </Button>
          <Button variant="outline" onClick={onClose}>Позже</Button>
        </div>
      </div>
    </div>
  );
}
